import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Shield, Zap, Eye, Bot, AlertTriangle, Lock, Send, Bell,
  Loader2, CheckCircle, XCircle, Globe, Key, Wifi, WifiOff,
  MapPin, Clock, RefreshCw, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

import { API } from '../config';

const AUTONOMY_LEVELS = [
  { level: 0, name: "Manual", description: "View only mode. System shows data and signals but places no orders.", icon: Eye, color: "text-muted-foreground", available: true },
  { level: 1, name: "Assisted", description: "Auto scanning and proposals. You approve every trade manually.", icon: Shield, color: "text-primary", available: true },
  { level: 2, name: "Semi-Auto", description: "Auto execution when all rules pass. Strict risk limits enforced.", icon: Zap, color: "text-warning", available: false, requiresAdmin: true },
  { level: 3, name: "Full Auto", description: "Autonomous trading for approved strategies only. Admin required.", icon: Bot, color: "text-success", available: false, requiresAdmin: true },
];

const Settings = () => {
  const { user, token, updateUser } = useAuth();
  const [autonomyLevel, setAutonomyLevel] = useState(user?.autonomy_level || 1);
  const [updating, setUpdating] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  // Telegram
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  // Binance Connection
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [useTestnet, setUseTestnet] = useState(false);
  const [savedKeys, setSavedKeys] = useState(null);
  const [savingKeys, setSavingKeys] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [regionCheck, setRegionCheck] = useState(null);
  const [checkingRegion, setCheckingRegion] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAuditLogs();
    fetchTelegramStatus();
    fetchSavedKeys();
    runRegionCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTelegramStatus = async () => {
    try {
      const res = await axios.get(`${API}/telegram/status`);
      setTelegramStatus(res.data);
    } catch {
      setTelegramStatus({ configured: false, connected: false });
    }
  };

  const fetchAuditLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API}/audit/logs?limit=20`);
      setAuditLogs(res.data);
    } catch { /* */ }
    setLogsLoading(false);
  };

  const fetchSavedKeys = async () => {
    try {
      const res = await axios.get(`${API}/live/saved-keys`, { headers });
      setSavedKeys(res.data);
      if (res.data.use_testnet !== undefined) setUseTestnet(res.data.use_testnet);
    } catch { /* */ }
  };

  const runRegionCheck = async () => {
    setCheckingRegion(true);
    try {
      const res = await axios.get(`${API}/live/region-check`, { headers });
      setRegionCheck(res.data);
    } catch { /* */ }
    setCheckingRegion(false);
  };

  const handleSaveKeys = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      toast.error("Both API Key and Secret are required.");
      return;
    }
    setSavingKeys(true);
    try {
      await axios.post(`${API}/live/save-keys`, {
        api_key: apiKey.trim(),
        api_secret: apiSecret.trim(),
        use_testnet: useTestnet,
      }, { headers });
      toast.success(`API keys saved (${useTestnet ? "Testnet" : "Live"} mode)`);
      setApiKey("");
      setApiSecret("");
      fetchSavedKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save keys");
    }
    setSavingKeys(false);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const res = await axios.post(`${API}/live/test-connection`, {}, { headers });
      setConnectionResult(res.data);
      if (res.data.status === "connected") {
        toast.success(`Connected! Balance: $${res.data.balance.toLocaleString()}`);
      } else if (res.data.status === "geo_blocked") {
        toast.error("Geo-restricted. Try testnet or deploy elsewhere.");
      } else {
        toast.error(res.data.error || "Connection failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection test failed");
    }
    setTestingConnection(false);
  };

  const handleLevelChange = async (level) => {
    if (level >= 2 && !user?.is_admin) {
      toast.error("Level 2+ requires admin approval");
      return;
    }
    setUpdating(true);
    try {
      await axios.put(`${API}/autonomy/level`, { level });
      setAutonomyLevel(level);
      updateUser({ autonomy_level: level });
      toast.success(`Autonomy level set to ${AUTONOMY_LEVELS[level].name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update autonomy level");
    }
    setUpdating(false);
  };

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    try {
      const res = await axios.post(`${API}/telegram/test`);
      if (res.data.success) {
        toast.success("Test message sent! Check your Telegram.");
        setTelegramStatus({ configured: true, connected: true });
      } else {
        toast.error(res.data.error || "Telegram test failed");
      }
    } catch {
      toast.error("Telegram test failed");
    }
    setTestingTelegram(false);
  };

  const handleSendDailySummary = async () => {
    setSendingSummary(true);
    try {
      const res = await axios.post(`${API}/telegram/daily-summary`);
      if (res.data.success) {
        toast.success(`Daily summary sent! ${res.data.trades_today} trades today, P&L: $${res.data.daily_pnl}`);
      } else {
        toast.error("Failed to send summary");
      }
    } catch {
      toast.error("Failed to send daily summary");
    }
    setSendingSummary(false);
  };

  const liveOk = regionCheck?.binance_live?.accessible;
  const testnetOk = regionCheck?.binance_testnet?.accessible;

  return (
    <div className="space-y-6" data-testid="settings">
      <div>
        <h1 className="text-2xl font-heading font-bold">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Binance connection, autonomy, alerts, and system preferences
        </p>
      </div>

      {/* ─── Binance Connection ─── */}
      <Card className="bg-zinc-900/60 border-cyan-800/30" data-testid="binance-connection-card">
        <CardHeader className="border-b border-zinc-800 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-zinc-400 flex items-center gap-2">
              <Globe className="w-4 h-4" /> Binance Connection
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={runRegionCheck}
                disabled={checkingRegion}
                className="h-7 text-xs border-zinc-700"
                data-testid="region-check-btn"
              >
                {checkingRegion ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Check Region
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {/* Region & Connectivity Status */}
          {regionCheck && (
            <div className="grid grid-cols-3 gap-3" data-testid="region-status">
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="text-[10px] text-zinc-500 uppercase">Server Location</span>
                </div>
                <p className="text-sm font-mono text-zinc-200">{regionCheck.server_country || "Unknown"}</p>
                <p className="text-[10px] text-zinc-500">{regionCheck.server_region || ""}</p>
              </div>
              <div className={`rounded-lg p-3 ${liveOk ? "bg-emerald-950/30" : "bg-red-950/20"}`}>
                <div className="flex items-center gap-2 mb-1">
                  {liveOk ? <Wifi className="w-3.5 h-3.5 text-emerald-400" /> : <WifiOff className="w-3.5 h-3.5 text-red-400" />}
                  <span className="text-[10px] text-zinc-500 uppercase">Binance Live</span>
                </div>
                <p className={`text-sm font-mono ${liveOk ? "text-emerald-400" : "text-red-400"}`}>
                  {liveOk ? "Accessible" : "Blocked"}
                </p>
                {regionCheck.binance_live?.latency_ms && (
                  <p className="text-[10px] text-zinc-500">{regionCheck.binance_live.latency_ms}ms latency</p>
                )}
                {!liveOk && regionCheck.binance_live?.error && (
                  <p className="text-[10px] text-red-400/70 mt-1">
                    {regionCheck.binance_live.error.includes("451") ? "Geo-restricted (451)" : regionCheck.binance_live.error.slice(0, 50)}
                  </p>
                )}
              </div>
              <div className={`rounded-lg p-3 ${testnetOk ? "bg-emerald-950/30" : "bg-red-950/20"}`}>
                <div className="flex items-center gap-2 mb-1">
                  {testnetOk ? <Wifi className="w-3.5 h-3.5 text-emerald-400" /> : <WifiOff className="w-3.5 h-3.5 text-red-400" />}
                  <span className="text-[10px] text-zinc-500 uppercase">Binance Testnet</span>
                </div>
                <p className={`text-sm font-mono ${testnetOk ? "text-emerald-400" : "text-red-400"}`}>
                  {testnetOk ? "Accessible" : "Blocked"}
                </p>
                {regionCheck.binance_testnet?.latency_ms && (
                  <p className="text-[10px] text-zinc-500">{regionCheck.binance_testnet.latency_ms}ms latency</p>
                )}
              </div>
            </div>
          )}

          {/* Geo-restriction notice */}
          {regionCheck && !liveOk && (
            <div className="bg-amber-950/20 border border-amber-700/30 rounded-lg p-3 text-xs text-amber-300/80" data-testid="geo-warning">
              <AlertTriangle className="w-4 h-4 inline mr-2 text-amber-400" />
              Binance Futures is <b>geo-restricted</b> from this server ({regionCheck.server_country || "unknown region"}).
              {testnetOk ? " Testnet is available for testing. For live trading, deploy from EU or use a VPN." : " Both live and testnet are blocked."}
            </div>
          )}

          {/* Saved Keys Status */}
          {savedKeys?.has_keys && (
            <div className="flex items-center justify-between bg-zinc-800/40 rounded-lg p-3" data-testid="saved-keys-status">
              <div className="flex items-center gap-3">
                <Key className="w-4 h-4 text-cyan-400" />
                <div>
                  <p className="text-sm text-zinc-200 font-mono">{savedKeys.api_key_masked}</p>
                  <p className="text-[10px] text-zinc-500">
                    Mode: {savedKeys.use_testnet ? "Testnet" : "Live"} | Updated: {savedKeys.updated_at ? new Date(savedKeys.updated_at).toLocaleDateString() : "---"}
                  </p>
                </div>
              </div>
              <Badge className={`text-[10px] ${savedKeys.use_testnet ? "bg-yellow-500/20 text-yellow-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                {savedKeys.use_testnet ? "TESTNET" : "LIVE"}
              </Badge>
            </div>
          )}

          {/* API Key Input */}
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">API Key</label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter Binance API Key"
                className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 font-mono focus:border-cyan-600 focus:outline-none placeholder:text-zinc-600"
                data-testid="binance-api-key-input"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">API Secret</label>
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Enter Binance API Secret"
                  className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-2 pr-10 text-sm text-zinc-200 font-mono focus:border-cyan-600 focus:outline-none placeholder:text-zinc-600"
                  data-testid="binance-api-secret-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useTestnet}
                  onChange={(e) => setUseTestnet(e.target.checked)}
                  className="w-4 h-4 rounded bg-zinc-800 border-zinc-600"
                  data-testid="testnet-checkbox"
                />
                Testnet Mode
                {useTestnet && <Badge className="text-[10px] bg-yellow-500/20 text-yellow-300">TESTNET</Badge>}
              </label>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveKeys}
                disabled={savingKeys || (!apiKey && !apiSecret)}
                className="bg-cyan-700 hover:bg-cyan-600 text-white"
                data-testid="save-keys-btn"
              >
                {savingKeys ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                Save Keys
              </Button>
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={testingConnection || !savedKeys?.has_keys}
                className="border-zinc-700 text-zinc-300"
                data-testid="test-connection-btn"
              >
                {testingConnection ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                Test Connection
              </Button>
            </div>
          </div>

          {/* Connection Test Result */}
          {connectionResult && (
            <div className={`rounded-lg p-3 text-sm ${
              connectionResult.status === "connected" ? "bg-emerald-950/30 border border-emerald-700/30" :
              connectionResult.status === "geo_blocked" ? "bg-amber-950/20 border border-amber-700/30" :
              "bg-red-950/20 border border-red-700/30"
            }`} data-testid="connection-result">
              {connectionResult.status === "connected" ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-emerald-300 font-medium">Connected to Binance {connectionResult.testnet ? "(Testnet)" : "(Live)"}</p>
                    <p className="text-[10px] text-zinc-500">Balance: ${connectionResult.balance?.toLocaleString()} | Latency: {connectionResult.latency_ms}ms</p>
                  </div>
                </div>
              ) : connectionResult.status === "geo_blocked" ? (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  <div>
                    <p className="text-amber-300 font-medium">Geo-restricted</p>
                    <p className="text-[10px] text-zinc-500">{connectionResult.error}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-red-300 font-medium">Connection Failed</p>
                    <p className="text-[10px] text-zinc-500">{connectionResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-[10px] text-zinc-600 space-y-1 border-t border-zinc-800 pt-3">
            <p>Keys are stored in the database and loaded automatically on engine start.</p>
            <p>For live trading, get keys from <a href="https://www.binance.com/en/my/settings/api-management" target="_blank" rel="noreferrer" className="text-cyan-500 hover:underline">binance.com/api-management</a></p>
            <p>For testnet, get keys from <a href="https://testnet.binancefuture.com" target="_blank" rel="noreferrer" className="text-cyan-500 hover:underline">testnet.binancefuture.com</a></p>
          </div>
        </CardContent>
      </Card>

      {/* Autonomy Levels */}
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Autonomy Level
            </CardTitle>
            <Badge variant="outline" className="font-mono">Current: Level {autonomyLevel}</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {AUTONOMY_LEVELS.map((level) => {
              const Icon = level.icon;
              const isActive = autonomyLevel === level.level;
              const isLocked = level.level >= 2 && !user?.is_admin;
              return (
                <div
                  key={level.level}
                  className={`p-4 border transition-all cursor-pointer ${
                    isActive ? 'border-primary bg-primary/5' : isLocked ? 'border-border opacity-50 cursor-not-allowed' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => !isLocked && handleLevelChange(level.level)}
                  data-testid={`autonomy-level-${level.level}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${isActive ? 'bg-primary/20' : 'bg-secondary'}`}>
                      <Icon className={`w-5 h-5 ${level.color}`} />
                    </div>
                    {isLocked && <Lock className="w-4 h-4 text-muted-foreground" />}
                    {isActive && <Badge className="bg-primary text-black text-xs">ACTIVE</Badge>}
                  </div>
                  <h3 className="font-semibold mb-1">Level {level.level}: {level.name}</h3>
                  <p className="text-sm text-muted-foreground">{level.description}</p>
                  {level.requiresAdmin && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-warning">
                      <AlertTriangle className="w-3 h-3" /> Requires admin approval
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Telegram Integration */}
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Telegram Notifications
            </CardTitle>
            <Badge
              variant="outline"
              className={`font-mono text-xs ${
                telegramStatus?.connected ? "border-emerald-500/50 text-emerald-400" :
                telegramStatus?.configured ? "border-amber-500/50 text-amber-400" :
                "border-zinc-600 text-zinc-500"
              }`}
              data-testid="telegram-status-badge"
            >
              {telegramStatus?.connected ? "CONNECTED" : telegramStatus?.configured ? "CONFIGURED" : "NOT SET UP"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Receive real-time trade alerts, daily P&L summaries, and risk warnings directly in Telegram via <b>@Skynet_yiannis_bot</b>.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              {telegramStatus?.configured ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-zinc-500" />}
              <span>Bot token configured</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {telegramStatus?.connected ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-zinc-500" />}
              <span>Connection verified</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleTestTelegram} disabled={testingTelegram || !telegramStatus?.configured} className="flex-1" data-testid="test-telegram-btn">
              {testingTelegram ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Testing...</> : <><Send className="w-3.5 h-3.5 mr-1.5" />Test Connection</>}
            </Button>
            <Button variant="outline" size="sm" onClick={handleSendDailySummary} disabled={sendingSummary || !telegramStatus?.configured} className="flex-1" data-testid="send-daily-summary-btn">
              {sendingSummary ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending...</> : <><Bell className="w-3.5 h-3.5 mr-1.5" />Send Daily Summary</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              System Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <InfoRow label="Operator ID" value={user?.username} />
            <InfoRow label="Email" value={user?.email} />
            <InfoRow label="Account Type" value={user?.is_admin ? "Admin" : "Operator"} />
            <InfoRow label="Mode" value="DIAMOND Paper Trading" highlight />
            <InfoRow label="Version" value="4.0" />
            <InfoRow label="Market Provider" value="Binance" />
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {logsLoading ? (
              <div className="text-center text-muted-foreground py-8">Loading...</div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">No activity yet</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {auditLogs.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-secondary/30 text-xs">
                    <span className="font-mono">{log.action.replace(/_/g, " ")}</span>
                    <span className="text-muted-foreground">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, highlight }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-mono ${highlight ? "text-primary" : ""}`}>{value}</span>
  </div>
);

export default Settings;
